import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CheckCircle2,
  PackageCheck,
  Printer,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getAdminOrderByNumber,
  resetAdminOrderError,
  resetAdminOrderMeta,
} from "../../../actions/adminOrderActions";

import Loader from "../../../components/Loader";
import Message from "../../../components/Message";
import Meta from "../../../components/Meta";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminOrderPrintScreen.css";

function safe(value = "") {
  return String(value || "").trim();
}

function humanize(value = "", fallback = "—") {
  const text = safe(value);

  if (!text) return fallback;

  return text
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatMoney(value = 0, currency = "GBP") {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safe(currency) || "GBP",
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    return `£${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
}

function formatDate(value = "") {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getOrderPayload(state = {}) {
  return state.order || state.details?.order || null;
}

function getPrimaryAddress(order = {}) {
  return (
    order.fulfillment?.shippingAddress ||
    order.shippingAddress ||
    order.billingAddress ||
    {}
  );
}

function getBillingAddress(order = {}) {
  return order.billingAddress || {};
}

function getPaymentReference(order = {}) {
  return (
    safe(order.payment?.providerPaymentId) ||
    safe(order.payment?.stripeCheckoutSessionId) ||
    safe(order.payment?.stripePaymentIntentId) ||
    safe(order.payment?.stripeChargeId) ||
    safe(order.payment?.paypalCaptureId) ||
    safe(order.payment?.paypalOrderId) ||
    "—"
  );
}

function getLineVariant(item = {}) {
  return [item.variantName, item.variantSku, item.variantType]
    .filter(Boolean)
    .join(" · ");
}

function AddressBlock({ title, address = {}, customer = {} }) {
  const lines = [
    address.fullName || customer.name,
    address.company,
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
    address.phone,
  ].filter(Boolean);

  return (
    <div className="admin-order-print__address">
      <h3>{title}</h3>

      {lines.length > 0 ? (
        lines.map((line) => <p key={`${title}-${line}`}>{line}</p>)
      ) : (
        <p>—</p>
      )}
    </div>
  );
}

function PrintRow({ label, value, strong = false }) {
  return (
    <div
      className={
        strong
          ? "admin-order-print__total-row is-strong"
          : "admin-order-print__total-row"
      }
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function AdminOrderPrintScreen() {
  const { orderNumber = "" } = useParams();

  const dispatch = useDispatch();

  const adminOrderState = useSelector(
    (state) => state.adminOrder || {},
    shallowEqual,
  );

  const { loading = false, error = null, pending = {} } = adminOrderState;

  const order = useMemo(
    () => getOrderPayload(adminOrderState),
    [adminOrderState],
  );

  const busy = Boolean(loading || pending.details);

  const loadOrder = useCallback(async () => {
    if (!orderNumber) return;

    try {
      await dispatch(getAdminOrderByNumber(orderNumber));
    } catch (err) {
      toast.error(
        getSafeUserMessage(
          err?.message,
          "We could not load this order right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, orderNumber]);

  useEffect(() => {
    loadOrder();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadOrder]);

  const shippingAddress = getPrimaryAddress(order || {});
  const billingAddress = getBillingAddress(order || {});
  const currency = order?.currency || "GBP";

  return (
    <main className="admin-order-print">
      <Meta
        title={`Print Order ${orderNumber} | Academy Costumes London`}
        description="Printable Academy Costumes London order document."
        robots="noindex, nofollow"
      />

      <section className="admin-order-print__toolbar">
        <Link
          to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
          className="admin-order-print__back"
        >
          <ArrowLeft size={16} />
          Back to order
        </Link>

        <div className="admin-order-print__toolbar-actions">
          <button
            type="button"
            className="admin-order-print__ghost-btn"
            onClick={loadOrder}
            disabled={busy}
          >
            <RefreshCw size={16} className={busy ? "is-spinning" : ""} />
            Refresh
          </button>

          <button
            type="button"
            className="admin-order-print__dark-btn"
            onClick={() => window.print()}
            disabled={!order}
          >
            <Printer size={16} />
            Print
          </button>
        </div>
      </section>

      {error ? <Message variant="danger">{error}</Message> : null}

      {loading && !order ? (
        <div className="admin-order-print__loader">
          <Loader />
        </div>
      ) : null}

      {!loading && !order ? (
        <section className="admin-order-print__empty">
          <PackageCheck size={38} />

          <h1>Order not found</h1>

          <p>This order could not be loaded.</p>

          <Link to="/admin/orders" className="admin-order-print__dark-btn">
            Return to orders
          </Link>
        </section>
      ) : null}

      {order ? (
        <article className="admin-order-print__sheet">
          <header className="admin-order-print__header">
            <div>
              <div className="admin-order-print__brand">
                <span>ACADEMY</span>
                <small>LONDON</small>
              </div>

              <p className="admin-order-print__brand-note">
                Order document / receipt copy
              </p>
            </div>

            <div className="admin-order-print__doc-meta">
              <h1>Order</h1>
              <p>{order.orderNumber || orderNumber}</p>
              <span>{formatDate(order.createdAt || order.placedAt)}</span>
            </div>
          </header>

          <section className="admin-order-print__status-strip">
            <div>
              <span>Order status</span>
              <strong>{humanize(order.status)}</strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>
                {humanize(order.payment?.status || "not_started")}
              </strong>
            </div>

            <div>
              <span>Provider</span>
              <strong>{humanize(order.payment?.provider || "—")}</strong>
            </div>

            <div>
              <span>Fulfilment</span>
              <strong>
                {humanize(order.fulfillment?.status || "pending")}
              </strong>
            </div>
          </section>

          <section className="admin-order-print__blocks">
            <div className="admin-order-print__block">
              <h2>Customer</h2>

              <p>
                <strong>{order.customer?.name || "—"}</strong>
              </p>

              <p>{order.customer?.email || "—"}</p>

              <p>
                {order.customer?.phone ||
                  billingAddress.phone ||
                  shippingAddress.phone ||
                  "—"}
              </p>
            </div>

            <AddressBlock
              title="Shipping / fulfilment"
              address={shippingAddress}
              customer={order.customer}
            />

            <AddressBlock
              title="Billing"
              address={billingAddress}
              customer={order.customer}
            />

            <div className="admin-order-print__block">
              <h2>Payment reference</h2>

              <p>
                <strong>{getPaymentReference(order)}</strong>
              </p>

              <p>
                {humanize(
                  order.payment?.method || order.payment?.provider || "—",
                )}
              </p>

              <p>
                Captured:{" "}
                {formatMoney(order.payment?.amountCaptured || 0, currency)}
              </p>
            </div>
          </section>

          <section className="admin-order-print__items-section">
            <h2>Items</h2>

            <div className="admin-order-print__table-wrap">
              <table className="admin-order-print__table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>SKU</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Cut status</th>
                    <th>Total</th>
                  </tr>
                </thead>

                <tbody>
                  {(order.items || []).map((item) => (
                    <tr
                      key={
                        item.id || item._id || item.lineId || item.productName
                      }
                    >
                      <td>
                        <strong>{item.productName}</strong>

                        <span>
                          {getLineVariant(item) || item.itemType || "Standard"}
                        </span>
                      </td>

                      <td>{item.variantSku || item.sku || "—"}</td>

                      <td>{item.qty || 0}</td>

                      <td>{formatMoney(item.unitPrice, currency)}</td>

                      <td>{humanize(item.cutStatus || "not_applicable")}</td>

                      <td>{formatMoney(item.lineTotal, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="admin-order-print__footer-grid">
            <div className="admin-order-print__notes">
              <h2>Notes</h2>

              <p>{order.notes || order.adminNotes || "No notes recorded."}</p>

              {order.policy?.statutoryRightsNote ? (
                <div className="admin-order-print__policy-note">
                  <CheckCircle2 size={15} />
                  <span>{order.policy.statutoryRightsNote}</span>
                </div>
              ) : null}
            </div>

            <div className="admin-order-print__totals">
              <PrintRow
                label="Subtotal"
                value={formatMoney(order.subtotal, currency)}
              />

              <PrintRow
                label="Shipping"
                value={formatMoney(order.shippingTotal, currency)}
              />

              <PrintRow
                label="Tax"
                value={formatMoney(order.taxTotal, currency)}
              />

              <PrintRow
                label={
                  order.promoCode?.code
                    ? `Discount (${order.promoCode.code})`
                    : "Discount"
                }
                value={formatMoney(order.discountTotal, currency)}
              />

              <PrintRow
                label="Refunded"
                value={formatMoney(
                  order.refundTotal ||
                    order.payment?.amountRefunded ||
                    order.refund?.amountRefunded ||
                    0,
                  currency,
                )}
              />

              <PrintRow
                label="Grand total"
                value={formatMoney(order.grandTotal, currency)}
                strong
              />
            </div>
          </section>

          <footer className="admin-order-print__footer">
            <span>Academy Costumes London</span>
            <span>Generated {formatDate(new Date())}</span>
          </footer>
        </article>
      ) : null}
    </main>
  );
}
